import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { IDLE, PENDING, VIEW, ARCHIVED } from "const"
import { StateType, hasAccess, deepCopy } from "client/utils"
import {
    PRM_OKR_OBJECTIVE_KEY_RESULT,
    StoreType,
    OkrKeyResultProps,
    OkrKeyResultList,

    LoadOkrKeyResultById,
    LoadOkrKeyResultUpdates,
    IsOwnerOkrObjective,
    IsOwnerOkrKeyResult,

    setOkrKeyResultList,
    setOkrKeyResultUpdateList,
    addModal,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    Button,
    Loader,
} from "client/components"

import AccessDenied from "client/parts/accessDenied"
import NotFound from "client/parts/notFound"
import {
    KeyResultLayout,
    KeyResultBodyColumn,
    KeyResultGraphColumn,
} from "client/pages/okr/styled"
import Form from "./form"
import KeyResultComments from "./comments"
import KeyResultUpdates from "./updates"
import KeyResultUpdateEdit from "./updates/edit"
import KeyResultGraph from "./graph"

// "View" page can be opened from key result list and from parent link.
// So, several "View" pages can be opened at the same time. And current "keyResultView" cannot be stored on Redux Store level
const KeyResultView = ({
    sessionId,
    objectiveId,
    keyResultId,
}: OkrKeyResultProps) => {
    const session = useSelector((state: StoreType) => state.okrSession.sessionView)
    const objectiveList = useSelector((state: StoreType) => state.okrObjective.objectiveList)
    const keyResultList = useSelector((state: StoreType) => state.okrKeyResult.keyResultList)

    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)

    useEffect(() => {
        setLoadState(PENDING)
        LoadOkrKeyResultById(keyResultId, objectiveId, sessionId).then((res) => {
            const keyResultListCopy: OkrKeyResultList = deepCopy(keyResultList)
            keyResultListCopy[keyResultId] = res
            dispatch(setOkrKeyResultList(keyResultListCopy))

            LoadOkrKeyResultUpdates(res).then((updates) => {
                dispatch(setOkrKeyResultUpdateList({ [keyResultId]: updates }))
                setLoadState(IDLE)
            })
        })
    }, [])

    const objective = objectiveList[objectiveId]
    const keyResult = keyResultList[keyResultId]

    const isOwnerKeyResult = IsOwnerOkrKeyResult(keyResult, userData.userId)
    const isOwnerObjective = IsOwnerOkrObjective(objective, userData.userId)
    const isOwnerSession = session.owner === userData.userId
    const isOwner = isOwnerKeyResult || isOwnerObjective || isOwnerSession

    const viewAccess = hasAccess(PRM_OKR_OBJECTIVE_KEY_RESULT, VIEW)
    const allowedToView = viewAccess || isOwner

    const isArchivedSession = session.status === ARCHIVED

    if (!allowedToView) return <AccessDenied />
    if (!objective || !keyResult) return <NotFound />
    return (
        <Container>
            <PageHeader>
                <PageTitle>{keyResult.title}</PageTitle>
                {
                    !isArchivedSession && (
                        <Button
                            variant="success"
                            onClick={() => dispatch(addModal({
                                key: "addUpdate",
                                content: <KeyResultUpdateEdit updateId={0} keyResult={keyResult} />,
                            }))}
                        >
                            {lang.update}
                        </Button>
                    )
                }
            </PageHeader>
            <PageContent>
                {
                    loadState === PENDING
                    && <Loader />
                }
                <KeyResultLayout>
                    <KeyResultBodyColumn>
                        <Form
                            objective={objective}
                            keyResult={keyResult}
                        />
                        <KeyResultComments keyResult={keyResult} />
                    </KeyResultBodyColumn>
                    <KeyResultGraphColumn>
                        <KeyResultGraph keyResult={keyResult} />
                        <KeyResultUpdates keyResult={keyResult} />
                    </KeyResultGraphColumn>
                </KeyResultLayout>
            </PageContent>
        </Container>
    )
}
export default KeyResultView
